import React, { useEffect, useState } from 'react';
import { MegaphoneIcon, ArrowLeftIcon, EditIcon, TrashIcon, InfoIcon, CalendarIcon, TagIcon, DollarSignIcon, PercentIcon, UserIcon, BuildingIcon, CreditCardIcon, BarChart2Icon, LineChartIcon, TrendingUpIcon, PieChartIcon, DownloadIcon, XIcon, CheckIcon } from 'lucide-react';
import { Campaign } from '../types/Campaign';
import { TimeSelector } from '../components/dashboard/TimeSelector';
import { ConversionFunnelChart, ConversionRatePieChart, CodeUsageTimeSeriesChart, PaymentVolumeTimeSeriesChart } from '../components/campaigns/CampaignCharts';
// Mock campaign data - would normally be fetched from API
const mockCampaigns: Record<string, Campaign> = {
  RBLSHOP2025: {
    id: 'RBLSHOP2025',
    title: '[RLife] 7-days Promotion for Shop',
    description: '[RLife] 7-days Promotion for Shop',
    expiryDate: '07/10/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: 'LKR',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 5000,
    budgetUsed: 0,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'RBLSHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active',
    companies: ['Rabbit Life'],
    transactionVolume: 0,
    transactionCount: 0
  },
  BBL10THB: {
    id: 'BBL10THB',
    title: 'BBL Special Package (Rs. 10 Discount)',
    description: 'DISCOUNT_COUPON',
    expiryDate: '02/08/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: 'LKR',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 11000,
    budgetUsed: 10960,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BBL10THB',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active'
  }
};
// Tab interface for analytics section
interface AnalyticsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}
export function CampaignDetails() {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('conversion');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTimeframe, setExportTimeframe] = useState('30d');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const analyticsTabs: AnalyticsTab[] = [{
    id: 'conversion',
    label: 'Conversion',
    icon: <BarChart2Icon size={16} data-id="element-3093" />
  }, {
    id: 'usage',
    label: 'Code Usage',
    icon: <LineChartIcon size={16} data-id="element-3094" />
  }, {
    id: 'volume',
    label: 'Payment Volume',
    icon: <TrendingUpIcon size={16} data-id="element-3095" />
  }];
  useEffect(() => {
    // In a real app, we would fetch the campaign data from an API
    // For now, we'll use the URL parameter to get the campaign ID
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      setCampaign(mockCampaigns[campaignId]);
    }
    setLoading(false);
  }, []);
  const navigateBack = () => {
    window.location.href = '/campaigns';
  };
  const navigateToEdit = () => {
    if (campaign) {
      window.location.href = `/campaign-form?id=${campaign.id}`;
    }
  };
  const handleExport = () => {
    setExportStatus('loading');
    // Simulate export process
    setTimeout(() => {
      setExportStatus('success');
      // Reset after showing success message
      setTimeout(() => {
        setExportStatus('idle');
        setShowExportModal(false);
      }, 2000);
    }, 1000);
  };
  // Calculate budget usage percentage
  const calculateBudgetUsage = () => {
    if (!campaign || campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-3096">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" data-id="element-3097"></div>
      </div>;
  }
  if (!campaign) {
    return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-3098">
        <h2 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-3099">
          Campaign not found
        </h2>
        <p className="text-gray-600 mb-4" data-id="element-3100">
          The campaign you are looking for does not exist or has been removed.
        </p>
        <button onClick={navigateBack} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3101">
          Back to Campaigns
        </button>
      </div>;
  }
  // Render analytics content based on active tab
  const renderAnalyticsContent = () => {
    switch (activeTab) {
      case 'conversion':
        return <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3102">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3103">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3104">
                Conversion Funnel
              </h3>
              <ConversionFunnelChart data-id="element-3105" />
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3106">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3107">
                Conversion Rates
              </h3>
              <ConversionRatePieChart data-id="element-3108" />
            </div>
          </div>;
      case 'usage':
        return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3109">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3110">
              Code Usage Over Time (Last 30 Days)
            </h3>
            <CodeUsageTimeSeriesChart data-id="element-3111" />
          </div>;
      case 'volume':
        return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3112">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3113">
              Payment Volume Over Time (Last 30 Days)
            </h3>
            <PaymentVolumeTimeSeriesChart currency={campaign.currency} data-id="element-3114" />
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-3115">
      {/* Top bar with actions */}
      <div className="flex justify-between items-center" data-id="element-3116">
        <button onClick={navigateBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-3117">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-3118" />
          <span data-id="element-3119">Back to Campaigns</span>
        </button>
        <div className="flex space-x-3" data-id="element-3120">
          <button onClick={() => setShowExportModal(true)} className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-3121">
            <DownloadIcon size={16} className="mr-2" data-id="element-3122" />
            Export CSV
          </button>
          <button onClick={navigateToEdit} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-3123">
            <EditIcon size={16} className="mr-2" data-id="element-3124" />
            Edit Campaign
          </button>
          <button className="px-3 py-2 bg-white border border-red-500 text-red-500 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center" data-id="element-3125">
            <TrashIcon size={16} className="mr-2" data-id="element-3126" />
            Delete
          </button>
        </div>
      </div>
      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-3127">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" data-id="element-3128">
            <div className="flex justify-between items-center mb-4" data-id="element-3129">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-3130">
                Export Campaign Data
              </h3>
              <button onClick={() => {
            setShowExportModal(false);
            setExportStatus('idle');
          }} className="text-gray-400 hover:text-gray-500" data-id="element-3131">
                <XIcon size={20} data-id="element-3132" />
              </button>
            </div>
            <div className="mb-4" data-id="element-3133">
              <p className="text-sm text-gray-600 mb-2" data-id="element-3134">
                Select time period for export:
              </p>
              <TimeSelector value={exportTimeframe} onChange={setExportTimeframe} data-id="element-3135" />
            </div>
            <div className="mb-4" data-id="element-3136">
              <p className="text-sm text-gray-600" data-id="element-3137">
                The export will include all campaign data for the selected
                period:
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-gray-600" data-id="element-3138">
                <li data-id="element-3139">Daily code usage statistics</li>
                <li data-id="element-3140">Conversion metrics</li>
                <li data-id="element-3141">Transaction volumes and amounts</li>
                <li data-id="element-3142">User engagement data</li>
              </ul>
            </div>
            <div className="flex justify-end" data-id="element-3143">
              {exportStatus === 'idle' && <button onClick={handleExport} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-3144">
                  <DownloadIcon size={16} className="mr-2" data-id="element-3145" />
                  Export
                </button>}
              {exportStatus === 'loading' && <button disabled className="px-4 py-2 bg-blue-600 text-white rounded-md opacity-70 flex items-center" data-id="element-3146">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" data-id="element-3147"></div>
                  Exporting...
                </button>}
              {exportStatus === 'success' && <button disabled className="px-4 py-2 bg-green-600 text-white rounded-md flex items-center" data-id="element-3148">
                  <CheckIcon size={16} className="mr-2" data-id="element-3149" />
                  Export Complete
                </button>}
              {exportStatus === 'error' && <button onClick={handleExport} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center" data-id="element-3150">
                  Try Again
                </button>}
            </div>
          </div>
        </div>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3151">
        <div className="flex items-center mb-6" data-id="element-3152">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4" data-id="element-3153">
            <MegaphoneIcon size={24} className="text-blue-600" data-id="element-3154" />
          </div>
          <div data-id="element-3155">
            <h1 className="text-2xl font-bold text-gray-800" data-id="element-3156">
              {campaign.title}
            </h1>
            <p className="text-gray-500" data-id="element-3157">Campaign ID: {campaign.id}</p>
          </div>
          <div className="ml-auto" data-id="element-3158">
            <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' : campaign.status === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-3159">
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </span>
          </div>
        </div>
        {/* Campaign statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-3160">
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-3161">
            <div className="flex items-center justify-between mb-4" data-id="element-3162">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-3163">
                Total Budget
              </h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-3164" />
            </div>
            <div className="flex items-center mb-2" data-id="element-3165">
              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-3166">
                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                width: `${calculateBudgetUsage()}%`
              }} data-id="element-3167"></div>
              </div>
              <span className="text-sm text-gray-500" data-id="element-3168">
                {calculateBudgetUsage()}%
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800" data-id="element-3169">
              {campaign.budgetUsed.toLocaleString('en-LK')} /{' '}
              {campaign.budget.toLocaleString('en-LK')} Rs.
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-3170">
            <div className="flex items-center justify-between mb-4" data-id="element-3171">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-3172">
                Transaction volume for campaign
              </h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-3173" />
            </div>
            <div className="text-2xl font-bold text-gray-800" data-id="element-3174">
              Rs.{' '}
              {campaign.transactionVolume?.toLocaleString('en-LK') || '0.00'}
            </div>
            <div className="text-xs text-gray-500 mt-1" data-id="element-3175">
              {campaign.transactionCount || 0} transactions have been collected
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-3176">
            <div className="flex items-center justify-between mb-4" data-id="element-3177">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-3178">KPI</h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-3179" />
            </div>
            <div className="space-y-4" data-id="element-3180">
              <div data-id="element-3181">
                <p className="text-xs text-gray-500" data-id="element-3182">Transaction volume ROI</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-3183">0</p>
                <p className="text-xs text-gray-500" data-id="element-3184">
                  Against a budget of Rs. 0.00
                </p>
              </div>
              <div data-id="element-3185">
                <p className="text-xs text-gray-500" data-id="element-3186">Transactions</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-3187">0</p>
                <p className="text-xs text-gray-500" data-id="element-3188">0 Merchants</p>
              </div>
            </div>
          </div>
        </div>
        {/* Campaign Analytics Section */}
        <div className="border-t border-gray-200 pt-6 mb-8" data-id="element-3189">
          <div className="flex justify-between items-center mb-6" data-id="element-3190">
            <h2 className="text-lg font-medium text-gray-800 flex items-center" data-id="element-3191">
              <PieChartIcon size={20} className="mr-2 text-blue-600" data-id="element-3192" />
              Campaign Analytics
            </h2>
          </div>
          {/* Analytics Tabs */}
          <div className="mb-6" data-id="element-3193">
            <div className="border-b border-gray-200" data-id="element-3194">
              <nav className="flex -mb-px space-x-8" data-id="element-3195">
                {analyticsTabs.map(tab => <button key={tab.id} className={`py-4 px-1 flex items-center text-sm font-medium ${activeTab === tab.id ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab(tab.id)} data-id="element-3196">
                    <span className="mr-2" data-id="element-3197">{tab.icon}</span>
                    {tab.label}
                  </button>)}
              </nav>
            </div>
          </div>
          {/* Analytics Content */}
          {renderAnalyticsContent()}
        </div>
        {/* Campaign details */}
        <div className="border-t border-gray-200 pt-6" data-id="element-3198">
          <div className="flex justify-between items-center mb-4" data-id="element-3199">
            <h2 className="text-lg font-medium text-gray-800" data-id="element-3200">
              Campaign details
            </h2>
            <button onClick={navigateToEdit} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-3201">
              <EditIcon size={14} className="mr-1" data-id="element-3202" />
              Edit details
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" data-id="element-3203">
            <div data-id="element-3204">
              <h3 className="text-sm text-gray-500" data-id="element-3205">Title</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3206">
                {campaign.title}
              </p>
            </div>
            <div data-id="element-3207">
              <h3 className="text-sm text-gray-500" data-id="element-3208">Category</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3209">
                {campaign.category}
              </p>
            </div>
            <div data-id="element-3210">
              <h3 className="text-sm text-gray-500" data-id="element-3211">Conditions</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3212">
                {campaign.conditions || 'No information'}
              </p>
            </div>
            <div data-id="element-3213">
              <h3 className="text-sm text-gray-500" data-id="element-3214">Expiry Date</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3215">
                {campaign.expiryDate}
              </p>
            </div>
            <div data-id="element-3216">
              <h3 className="text-sm text-gray-500" data-id="element-3217">Description</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3218">
                {campaign.description}
              </p>
            </div>
            <div data-id="element-3219">
              <h3 className="text-sm text-gray-500" data-id="element-3220">Campaign Owner</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3221">
                {campaign.owner}
              </p>
            </div>
            <div data-id="element-3222">
              <h3 className="text-sm text-gray-500" data-id="element-3223">Code Strategy</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3224">
                {campaign.codeStrategy}
              </p>
            </div>
            <div data-id="element-3225">
              <h3 className="text-sm text-gray-500" data-id="element-3226">Min. Transaction Value</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3227">
                Rs.{' '}
                {campaign.minTransactionValue.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-3228">
              <h3 className="text-sm text-gray-500" data-id="element-3229">Max. Transaction Value</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3230">
                Rs. {campaign.maxTransactionValue.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-3231">
              <h3 className="text-sm text-gray-500" data-id="element-3232">Min. Discount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3233">
                Rs.{' '}
                {campaign.minDiscount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-3234">
              <h3 className="text-sm text-gray-500" data-id="element-3235">Max. Discount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3236">
                Rs.{' '}
                {campaign.maxDiscount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-3237">
              <h3 className="text-sm text-gray-500" data-id="element-3238">Valid MCC</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3239">
                {campaign.validMCC?.join(', ') || 'All MCCs'}
              </p>
            </div>
            <div data-id="element-3240">
              <h3 className="text-sm text-gray-500" data-id="element-3241">Valid on days</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3242">
                {campaign.validDays.length === 7 ? 'All days' : campaign.validDays.join(', ')}
              </p>
            </div>
            <div data-id="element-3243">
              <h3 className="text-sm text-gray-500" data-id="element-3244">Buyer Identifier</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3245">
                {campaign.validIdentities?.join(', ') || 'No information'}
              </p>
            </div>
            <div data-id="element-3246">
              <h3 className="text-sm text-gray-500" data-id="element-3247">Valid Identities</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3248">
                {campaign.validIdentities?.join(', ') || 'All identities'}
              </p>
            </div>
            <div data-id="element-3249">
              <h3 className="text-sm text-gray-500" data-id="element-3250">Code</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3251">
                {campaign.code}
              </p>
            </div>
            <div data-id="element-3252">
              <h3 className="text-sm text-gray-500" data-id="element-3253">Currency</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3254">LKR (Rs.)</p>
            </div>
            <div data-id="element-3255">
              <h3 className="text-sm text-gray-500" data-id="element-3256">Discount Percentage</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3257">
                {campaign.discountPercentage}%
              </p>
            </div>
            <div data-id="element-3258">
              <h3 className="text-sm text-gray-500" data-id="element-3259">Fixed discount amount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3260">
                Rs.{' '}
                {campaign.fixedDiscountAmount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-3261">
              <h3 className="text-sm text-gray-500" data-id="element-3262">Discount carried by</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3263">
                {campaign.discountCarriedBy}
              </p>
            </div>
            <div data-id="element-3264">
              <h3 className="text-sm text-gray-500" data-id="element-3265">
                Discount percentage carried by merchant
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3266">
                {campaign.merchantPercentage || 0}%
              </p>
            </div>
            <div data-id="element-3267">
              <h3 className="text-sm text-gray-500" data-id="element-3268">Budget</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3269">
                Rs. {campaign.budget.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-3270">
              <h3 className="text-sm text-gray-500" data-id="element-3271">Budget used</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3272">
                Rs. {campaign.budgetUsed.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-3273">
              <h3 className="text-sm text-gray-500" data-id="element-3274">Recurring budget</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3275">
                {campaign.recurringBudget ? `Rs. ${campaign.recurringBudget.toLocaleString('en-LK')}` : 'None'}
              </p>
            </div>
            <div data-id="element-3276">
              <h3 className="text-sm text-gray-500" data-id="element-3277">Recurring budget used</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3278">
                {campaign.recurringBudgetUsed ? `Rs. ${campaign.recurringBudgetUsed.toLocaleString('en-LK')}` : 'None'}
              </p>
            </div>
            <div data-id="element-3279">
              <h3 className="text-sm text-gray-500" data-id="element-3280">Recurring budget period</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3281">
                {campaign.recurringBudgetPeriod || 'No data available'}
              </p>
            </div>
            <div data-id="element-3282">
              <h3 className="text-sm text-gray-500" data-id="element-3283">
                Strictly enforce budget?
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3284">
                {campaign.strictlyEnforceBudget ? 'Yes' : 'No'}
              </p>
            </div>
            <div data-id="element-3285">
              <h3 className="text-sm text-gray-500" data-id="element-3286">Companies</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3287">
                {campaign.companies?.join(', ') || 'All companies'}
              </p>
            </div>
            <div data-id="element-3288">
              <h3 className="text-sm text-gray-500" data-id="element-3289">
                Limit usage per total customer
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3290">
                {campaign.limitUsage || 'No information'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}